'use client';

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../cn';

interface ComboboxOption {
  value: string | number;
  label: string;
}

interface ReusableComboboxProps {
  options: ComboboxOption[];
  value?: string | number;
  onValueChange?: (value: string | number) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  disabled?: boolean;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  onInputChange?: (value: string) => void;
}

export function ReusableCombobox({
  options,
  value,
  onValueChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  emptyText = 'No option found.',
  className,
  disabled = false,
  open: controlledOpen,
  setOpen: setControlledOpen,
  onInputChange,
}: ReusableComboboxProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setOpen = setControlledOpen || setUncontrolledOpen;

  // Add local state for search input value
  const [searchValue, setSearchValue] = React.useState('');

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between', className)}
          disabled={disabled}
        >
          {options?.find(option => String(option.value) === String(value))?.label || placeholder}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[450px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            className="h-9"
            value={searchValue}
            onValueChange={val => {
              setSearchValue(val);
              onInputChange?.(val);
            }}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map(option => (
                <CommandItem
                  key={option?.value}
                  value={String(option?.value)}
                  onSelect={() => {
                    onValueChange?.(option?.value);
                    setOpen(false);
                    setSearchValue('');
                    onInputChange?.('');
                  }}
                >
                  {option?.label}
                  <Check
                    className={cn(
                      'ml-auto',
                      String(value) === String(option.value) ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
